import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Trans } from '@lingui/macro';
import { Spinner } from '@toqio/toqio-web-components';
import { PrimaryButton } from '../../../shared/components';

type Props = {
  open: boolean;
  onContinue: () => void;
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(20, 27, 52, 0.35);
  backdrop-filter: blur(2px);
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  width: 100%;
  max-width: 440px;
  padding: 40px 32px;
  border-radius: 28px;
  background: ${({ theme }) =>
    (theme.colors?.cards_default_background_color as string | undefined) ?? '#fff'};
  box-shadow: 0 24px 64px rgba(20, 27, 52, 0.18);
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
`;

const IconCircle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;

  /* Only the lavender circle pulses; the square icon stays still. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${({ theme }) =>
    (theme.colors?.icons_default_background_color as string | undefined) ?? '#ecebfb'};
    animation: ${pulse} 1.8s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }
`;

const IconSquare = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 18px;
  color: #fff;
  background: ${({ theme }) =>
    (theme.colors?.primary_button_default_background_color as string | undefined) ?? '#141b34'};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.25;
  color: ${({ theme }) =>
    (theme.colors?.forms_default_font as string | undefined) ?? '#141b34'};
`;

const Body = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) =>
    (theme.colors?.forms_default_secondary_font as string | undefined) ?? '#5a6072'};
`;

const Cta = styled(PrimaryButton)`
  width: 100%;
`;

const Waiting = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) =>
    (theme.colors?.placeholders_box_text_color as string | undefined) ?? '#8a90a2'};
  font-size: 15px;
`;

/** Phone / mobile-device glyph — the DS Icon set lacks it (see icons pattern
 * in pay-by-bank-backoffice/components/icons.tsx). */
const PhoneIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect
      x="6"
      y="2.5"
      width="12"
      height="19"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M10.5 18h3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Push-authorisation modal (SEB-1714).
 *
 * Shown when `POST /payments` returns the decoupled-auth scenario
 * (`AWAITING_DECOUPLED_AUTHORISATION`, no `redirectUrl`): the payer must
 * authorise on their device via a push notification, then tap the CTA to
 * finish. Manual-only by design (SEB-1712) — the spinner + waiting line are
 * decorative, there is no polling. The CTA is the only exit, so the overlay
 * is intentionally non-dismissable.
 */
export const AuthorizationRequiredModal: React.FC<Props> = ({ open, onContinue }) => {
  if (!open) return null;

  return (
    <Overlay role="dialog" aria-modal="true" aria-labelledby="auth-required-title">
      <Card>
        <IconCircle>
          <IconSquare>
            <PhoneIcon />
          </IconSquare>
        </IconCircle>
        <Title id="auth-required-title">
          <Trans>Authorise the payment with your bank</Trans>
        </Title>
        <Body>
          <Trans>
            We&apos;ve sent a notification to your mobile device. Confirm the
            operation and, once you have, tap the button to finish.
          </Trans>
        </Body>
        <Cta onClick={onContinue}>
          <Trans>I&apos;ve already authorised the payment</Trans>
        </Cta>
        <Waiting>
          <Spinner size={18} />
          <Trans>Waiting for confirmation from your bank…</Trans>
        </Waiting>
      </Card>
    </Overlay>
  );
};
